import { EmployeeProfileModel } from '@herbjoy/apex-api-models';
import { ILogService, IPromise, IQService, IScope } from 'angular';
import { StateService } from 'angular-ui-router';
import { EmployeeService, LocaleService } from '../../../core/services';
import { UtilityService } from '../../../core/services/utility.service';
import templateUrl from './edit-employee-bank-info-container.component.html';

export const EditEmployeeBankInfoContainerComponent = {
    templateUrl,
    controller: EditEmployeeBankInfoContainerController,
    controllerAs: "vm",
    bindings: {
        employeeId: "<",
        identity: "<"
    }
};

function EditEmployeeBankInfoContainerController($log: ILogService, $q: IQService,
    $scope: IScope, $state: StateService, utilityService: UtilityService,
    employeeService: EmployeeService, localeService: LocaleService, stateProvinceService) {
    'ngInject';
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const vm = this;

    vm.employeeId;
    vm.MaritalStatusOptions;
    vm.$onInit = onInit;
    vm.onSubmit = onSubmit;
    vm.employeeBankInfo;
    vm.canAddUser;
    vm.originalSSN;
    vm.originalFEIN;
    vm.ssnIsValidated = ssnIsValidated;
    vm.feinIsValidated = feinIsValidated;
    vm.ssnBlur = ssnBlur;
    vm.feinBlur = feinBlur;
    vm.isSiteAdmin;
    vm.loggedInUserID;
    vm.onChangeWithholdingAmount = onChangeWithholdingAmount;


    function onInit() {
        vm.loggedInUserID = vm.identity.EmployeeID;
        utilityService.showLoading();
        refreshScreen().catch(function (ex) {
            $log.error(ex);
            alert("Initial load failed: " + utilityService.getExceptionMessage(ex));
        }).then(function () {
            utilityService.hideLoading();
        });
    }

    function refreshScreen() {
        const userIdentity = retrieveIdentity()
            .then(function (identity) {
                vm.identity = identity;
                vm.isSiteAdmin = identity.IsSiteAdmin;
                vm.canAddUser = true; // Enable globally for now.
            });

        const retrieveMaritalStatusList = getMaritalStatusList()
            .then(function (maritalStatusOptions) {
                vm.maritalStatusOptions = maritalStatusOptions;
            });

        const retrieveProvinceStateList = getProvinceStateList()
            .then(function (provinceStateOptions) {
                vm.provinceStateOptions = provinceStateOptions;
            });

        const withholdingListLoaded = getWithHoldingList()
            .then(function (withHoldingOptions) {
                vm.withHoldingOptions = withHoldingOptions;
            });

        const employeeBankInfo = $q.all([withholdingListLoaded, getEmployeeBankInfo()])
            .then(([_, employeeBankInfo]) => {
                for (const withholdingInfo of vm.withHoldingOptions) {
                    withholdingInfo.withholdingAmount = employeeBankInfo.EmployeeWithholdingInfos
                        .filter(p => withholdingInfo.WithholdingTypeID === p.WithholdingTypeID)
                        .map(p => p.AdditionalWithholdingAmount)[0];
                }
                vm.employeeBankInfo = employeeBankInfo;
                vm.employeeBankInfo.originalSSN = vm.employeeBankInfo.SSN;
                vm.employeeBankInfo.originalFEIN = vm.employeeBankInfo.FEIN;
                vm.employeeBankInfo.taxNumberDisplayed = vm.employeeBankInfo.CurrentFEIN != null ? "fein" : "ssn";
                return getEmployeeProfile(vm.employeeId)
            })
            .then(function (employeeProfile) {
                vm.employeeBankInfo.CountryID = employeeProfile.HomeAddress.CountryID;
                vm.employeeBankInfo.ShowRosterManagerView = employeeProfile.ShowRosterManagerView;
                vm.employeeBankInfo.EmployeeName = employeeProfile.FirstName + ' ' + employeeProfile.LastName;
                vm.employeeBankInfo.CompensationProfileID = employeeProfile.CompensationProfile
                    ? employeeProfile.CompensationProfile.Id : null;
                vm.isCanadian = employeeProfile.HomeAddress.CountryID === 2;
                return retrieveLocale();
            })
            .then(function (locale) {
                vm.employeeBankInfo.LocaleID = locale.localeID;
                vm.employeeBankInfo.ShowMaritalStatus = vm.employeeBankInfo.CountryID === 1;
                return retrieveLocaleTranslations(locale.localeID);
            })
            .then(function (translations) {
                vm.employeeBankInfo.Translations = translations;
                return getCompensationProfiles();
            })
            .then(function (compensationProfiles) {
                const filteredCompensationProfile = compensationProfiles.filter(function (compensationProfileObj) {
                    return compensationProfileObj.Id === vm.employeeBankInfo.CompensationProfileID;
                })[0];
                vm.is1099 = filteredCompensationProfile && filteredCompensationProfile.Name === "900 1099";
            });

        return $q.all([employeeBankInfo, userIdentity, retrieveMaritalStatusList, withholdingListLoaded,
            retrieveProvinceStateList])
            .then(function (result) {
                return result;
            });
    }

    function retrieveLocale() {
        return localeService.retrieveLocales()
            .then(function (locales) {
                return locales.filter(function (locale) {
                    return locale.countryID === vm.employeeBankInfo.CountryID;
                })[0] || localeService.retrieveCurrentLocale();
            })
            .then(function (locale) {
                if (locale) {
                    return locale;
                }
            });
    }

    function retrieveIdentity() {
        return employeeService.retrieveIdentity()
            .then(function (identity) {
                return identity;
            });
    }

    function retrieveLocaleTranslations(localeID) {
        return localeService.retrieveLocaleTranslations(localeID)
            .then(function (translations) {
                return translations;
            });
    }

    function getEmployeeBankInfo() {
        return employeeService.retrieveEmployeeBankInfo(vm.employeeId)
            .then(function (employeeBankInfo) {
                return employeeBankInfo;
            });
    }

    function getEmployeeProfile(employeeID: number): IPromise<EmployeeProfileModel> {
        return employeeService.retrieveEmployeeProfile(employeeID)
            .then(function (employeeProfile) {
                return employeeProfile;
            })
            .catch(ex => {
                if (ex.status === 403) {
                    // User doesn't have permission to read this account.
                    $state.go("persisted403");
                    // Never resolve. The transition will take care of the follow-up logic.
                    return $q.defer<EmployeeProfileModel>().promise;
                }
                throw ex;
            });
    }

    function getMaritalStatusList() {
        return employeeService.retrieveMaritalStatuses()
            .then(function (maritalStatusList) {
                return maritalStatusList;
            });
    }

    function getWithHoldingList() {
        return employeeService.retrieveWithHolding()
            .then(function (withHoldingList) {
                return withHoldingList;
            });
    }

    function getProvinceStateList() {
        return stateProvinceService.retrieveStateProvinces()
            .then(function (provinceState) {
                return provinceState;
            });
    }

    function getCompensationProfiles() {
        return employeeService.retrieveCompensationProfiles()
            .then(function (compensationProfile) {
                return compensationProfile;
            });
    }

    function onChangeWithholdingAmount({ $event }) {
        let itemExists = false;
        for (const p of vm.employeeBankInfo.EmployeeWithholdingInfos) {
            if (p.WithholdingTypeID === $event.withholdingType.WithholdingTypeID) {
                p.AdditionalWithholdingAmount = $event.withholdingType.withholdingAmount;
                itemExists = true;
            }
        }
        if (!itemExists) {
            const withholdingInfo = {
                EmployeeID: vm.employeeBankInfo.EmployeeID,
                WithholdingTypeID: $event.withholdingType.WithholdingTypeID,
                AdditionalWithholdingAmount: $event.withholdingType.withholdingAmount
            }
            return vm.employeeBankInfo.EmployeeWithholdingInfos.push(withholdingInfo);
        }
    }

    function onSubmit() {
        let save;

        if (vm.editForm && vm.editForm.$invalid) {
            alert("Form is invalid. Fill the required fields.");
            return;
        }

        if (vm.employeeBankInfo.SSN) {
            if (vm.employeeBankInfo.SSN !== vm.employeeBankInfo.confirmSSN) {
                alert("Please confirm SSN");
                return;
            }
        }

        if (vm.employeeBankInfo.FEIN) {
            if (vm.employeeBankInfo.FEIN !== vm.employeeBankInfo.confirmFEIN) {
                alert("Please confirm FEIN");
                return;
            }
        }

        if (vm.employeeBankInfo.EmployeeID) {
            save = employeeService.updateEmployeeBankInfo(vm.employeeBankInfo.EmployeeID, vm.employeeBankInfo);
            utilityService.showLoadingUntilResolved(
                save.then(function () {
                    if (vm.editForm) {
                        vm.editForm.$setPristine(true);
                    }
                    if (vm.employeeBankInfo.EmployeeID !== vm.loggedInUserID) {
                        return $state.go("roster");
                    } else {
                        return $state.go("home");
                    }
                }).catch(function (ex) {
                    vm.modelState = ex.data ? ex.data.ModelState : null;
                    if (vm.modelState) {
                        const messages = utilityService.getModelStateErrors(vm.modelState)
                        // If the model validation failed, the error is lower severity, so don't log as error.
                        $log.warn("Server side validation failed.", messages);
                        alert(messages.join());
                    } else {
                        $log.error(ex);
                        alert("Save failed: " + utilityService.getExceptionMessage(ex));
                    }
                })
            );
        }
    }

    function ssnIsValidated() {
        if (vm.employeeBankInfo.ssnChanged) {
            if ((vm.employeeBankInfo.SSN || "") !== (vm.employeeBankInfo.confirmSSN || "")) {
                return false;
            }
        }
        return true;
    }

    function feinIsValidated() {
        if (vm.employeeBankInfo.feinChanged) {
            if ((vm.employeeBankInfo.FEIN || "") !== (vm.employeeBankInfo.confirmFEIN || "")) {
                return false;
            }
        }
        return true;
    }

    function ssnBlur() {
        if (vm.employeeBankInfo.SSN !== vm.employeeBankInfo.originalSSN) {
            vm.employeeBankInfo.ssnChanged = true;
        } else {
            vm.employeeBankInfo.ssnChanged = false;
        }
    }

    function feinBlur() {
        if (vm.employeeBankInfo.FEIN !== vm.employeeBankInfo.originalFEIN) {
            vm.employeeBankInfo.feinChanged = true;
        } else {
            vm.employeeBankInfo.feinChanged = false;
        }
    }

    vm.revealSSNChar = function (ssn, char, i) {
        return i > 4;
    }
    vm.revealFEINChar = function (fein, char, i) {
        return i > 4;
    }
}
